import React, { useState, useEffect, useContext } from 'react';
import { axiosInstance } from '../../config';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import navlogomobile from './logosingle.png';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import { AuthContext } from '../../context/AuthContext';
import './profile.css';

export default function Profile({ username: propUsername }) {
  const { user: currentUser } = useContext(AuthContext);

  const [profileTitle, setProfileTitle] = useState('');
  const [verifiedStatus, setverifiedStatus] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profilepic, setProfilePic] = useState('');
  const [backgroundpic, setBackgroundPic] = useState('');
  const [userLinks, setUserLinks] = useState([]);
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  const [backgroundType, setBackgroundType] = useState('');
  const [user, setUser] = useState(null);
  const { username } = useParams();

  const loc = useLocation();
  const usernameToDisplay = propUsername || (currentUser && currentUser.username);
  const lowercaseUsername = usernameToDisplay ? usernameToDisplay.toLowerCase() : '';

  const [premiumStatus, setpremiumStatus] = useState('');
  const [logo, setLogo] = useState();
  const [isProfilePicLoading, setIsProfilePicLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [subscriptionPrice, setSubscriptionPrice] = useState(null);
  const [subscriptionDescription, setSubscriptionDescription] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const nav = useNavigate();

  const backgroundStyle = {
    backgroundImage: `url(${backgroundpic})`,
    backgroundColor: backgroundColor,
    backgroundSize: 'cover',
  };

  const fetchUserData = async () => {
    try {
      setIsLoading(true);

      if (!propUsername && currentUser) {
        setUser(currentUser);

        const profileDataResponse = await axiosInstance.get(`/users/profileTitleFetch?username=${currentUser.username}`);
        setProfileTitle(profileDataResponse.data.profileTitle);
        setverifiedStatus(currentUser.bluecheck);
        setpremiumStatus(currentUser.goldcheck);

        const bioDataResponse = await axiosInstance.get(`/users/profileBioFetch?username=${currentUser.username}`);
        setProfileBio(bioDataResponse.data.profileBio);
        setBackgroundColor(profileDataResponse.data.backgroundColor);

        const userLinksResponse = await axiosInstance.get(`/userlinksFetch?username=${currentUser.username}`);
        setUserLinks(userLinksResponse.data.links);
        setLogo(profileDataResponse.data.logo);
        setBackgroundType(profileDataResponse.data.backgroundType);

        const creatorSettingsResponse = await axiosInstance.get(`/users/creator-settings/${currentUser._id}`);
        setSubscriptionEnabled(creatorSettingsResponse.data.subscriptionEnabled);
        setSubscriptionPrice(creatorSettingsResponse.data.subscriptionPrice);
        setSubscriptionDescription(creatorSettingsResponse.data.subscriptionDescription);
      } else {
        const userResponse = await axiosInstance.get(`/users?username=${lowercaseUsername}`);
        if (userResponse.data.length === 0) {
          setUser(null);
        } else {
          const userData = userResponse.data;
          setUser(userData);

          const profileDataResponse = await axiosInstance.get(`/users/profileTitleFetch?username=${lowercaseUsername}`);
          setProfileTitle(profileDataResponse.data.profileTitle);
          setverifiedStatus(userResponse.data.bluecheck);
          setpremiumStatus(userResponse.data.goldcheck);

          const bioDataResponse = await axiosInstance.get(`/users/profileBioFetch?username=${lowercaseUsername}`);
          setProfileBio(bioDataResponse.data.profileBio);
          setBackgroundColor(profileDataResponse.data.backgroundColor);

          const userLinksResponse = await axiosInstance.get(`/userlinksFetch?username=${lowercaseUsername}`);
          setUserLinks(userLinksResponse.data.links);
          setLogo(profileDataResponse.data.logo);
          setBackgroundType(profileDataResponse.data.backgroundType);

          const creatorSettingsResponse = await axiosInstance.get(`/users/creator-settings/${userData._id}`);
          setSubscriptionEnabled(creatorSettingsResponse.data.subscriptionEnabled);
          setSubscriptionPrice(creatorSettingsResponse.data.subscriptionPrice);
          setSubscriptionDescription(creatorSettingsResponse.data.subscriptionDescription);

          if (currentUser) {
            try {
              const subRes = await axiosInstance.get(`/users/check-subscription/${userData._id}`);
              setIsSubscribed(subRes.data.isSubscribed);
            } catch (error) {
              console.error('Error checking subscription status:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setIsSubscribing(true);
      const response = await axiosInstance.post('/v1/stripe/create-subscription', {
        creatorId: user._id,
        ...(currentUser && { userId: currentUser._id }),
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('This is a Demo. You cannot subscribe to this creator.');
      setIsSubscribing(false);
    }
  };

  useEffect(() => {
    const fetchProfilePic = async () => {
      try {
        setIsProfilePicLoading(true);

        if (!propUsername && currentUser) {
          if (currentUser.profilePicture) {
            setProfilePic(currentUser.profilePicture);
          } else {
            setProfilePic(null);
          }
        } else {
          const response = await axiosInstance.get(`/users/getusernameprofilepicture/${lowercaseUsername}`, { responseType: 'blob' });

          if (response.status === 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            setProfilePic(url);
          }
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      } finally {
        setIsProfilePicLoading(false);
      }
    };

    fetchProfilePic();
  }, [usernameToDisplay, lowercaseUsername, propUsername, currentUser]);

  useEffect(() => {
    const fetchBackgroundPic = async () => {
      try {
        const response = await axiosInstance.get(`/users/getuserbackgroundpicture/${lowercaseUsername}`, { responseType: 'blob' });
        if (response.status === 200) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          setBackgroundPic(url);
        }
      } catch (error) {
        console.error('Error fetching background picture:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBackgroundPic();
  }, [usernameToDisplay, lowercaseUsername]);

  useEffect(() => {
    fetchUserData();
  }, [usernameToDisplay, lowercaseUsername, propUsername, currentUser]);

  useEffect(() => {
    const handleSubscriptionSuccess = async () => {
      const sessionId = new URLSearchParams(window.location.search).get('session_id');
      if (sessionId) {
        if (!currentUser) {
          window.location.href = `/signup?session_id=${sessionId}&creator=${usernameToDisplay}`;
        } else {
          setShowSuccessAlert(true);
        }
      }
    };

    handleSubscriptionSuccess();
  }, [currentUser, usernameToDisplay]);

  // Back icon
  function ArrowLeftIcon({ size = 18 }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
    );
  }

  /* ---- SMART BACK (renamed so header can call it) ---- */
  const goBackToForYou = () => {
    const backTo = loc.state?.backTo;
    if (backTo) return nav(backTo, { replace: true });
    if (window.history.length > 1) return nav(-1);
    nav(`/`, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <CircularProgress size={50} style={{ color: 'white' }} />
      </div>
    );
  }

  // ----- ROUTE: /profile (no username prop) -----
  if (!propUsername) {
    return (
      <div className="streaming-page">
        {/* Top back bar */}
        <header className="page-topbar">
          <button
            type="button"
            className="topbar-back"
            aria-label="Back"
            onClick={goBackToForYou}
          >
            <ArrowLeftIcon />
          </button>
          <div className="page-topbar-title">Profile</div>
        </header>

        {/* Content */}
        <div className="profile-page">
          <div className="profile-container">
            <div className="profile-avatar-section">
              <div className="avatar-display">
                {isProfilePicLoading ? (
                  <CircularProgress size={60} style={{ color: '#000000' }} />
                ) : profilepic ? (
                  <img src={profilepic} alt="Profile" className="current-avatar" />
                ) : (
                  <div className="default-avatar">
                    {currentUser ? currentUser.username.charAt(0).toUpperCase() : '@'}
                  </div>
                )}
              </div>

            </div>

            <div className="profile-info">
              <div className="profile-name">{profileTitle}</div>
              <div className="profile-bio">{profileBio}</div>
            </div>
          </div>

          <div className="profile-footer">
            <div className="footer-text">{/* reserved */}</div>
          </div>

          <Snackbar
            open={showSuccessAlert}
            autoHideDuration={4000}
            onClose={() => setShowSuccessAlert(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
              Profile picture updated!
            </Alert>
          </Snackbar>
        </div>
      </div>
    );
  }

  // ----- ROUTE: /:username profile view -----
  return (
    <div className="streaming-page">
      {/* Top back bar */}
      <header className="page-topbar">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={goBackToForYou}
        >
          <ArrowLeftIcon />
        </button>
        <div className="page-topbar-title">Profile</div>
      </header>

      <div className="profile-container1">
        <Helmet>{/* seo tags (kept commented as in your original) */}</Helmet>

        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={6000}
          onClose={() => setShowSuccessAlert(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
            Successfully subscribed to {profileTitle}!
          </Alert>
        </Snackbar>

        {backgroundType === 'FlatColour' && (
          <div className="profile-container1" style={{ backgroundColor: backgroundColor }}>
            <section className="animated-background">
              <div id="stars1"></div>
              <div id="stars2"></div>
              <div id="stars3"></div>
            </section>

            <a id="profilePicture">
              {isProfilePicLoading ? (
                <CircularProgress size={30} style={{ color: 'white' }} />
              ) : profilepic ? (
                <img src={profilepic} alt="Profile" />
              ) : (
                <p className="profilenulltext">@</p>
              )}
            </a>

            <div className="overlay" id="popup">
              <div className="popup">
                <div className="popup-photo">
                  <a
                    href="https://avatars0.githubusercontent.com/u/43749971"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://pbs.twimg.com/profile_images/1700343203616940032/Y0KMbn_z_400x400.jpg"
                      alt="ProfilePicture"
                    />
                  </a>
                </div>
                <div className="popup-quote">We locked in.</div>
                <button className="popup-close" onClick={() => window.history.back()}>
                  &times;
                </button>
              </div>
            </div>

            <div id="userName" className="userName">
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>{profileTitle}</span>
              {verifiedStatus && (
                <img src="https://xsubscribe.bio/bluecheck.svg" alt="Blue Check Mark" className="blue-check-mark" />
              )}
              {premiumStatus && (
                <img src="https://xsubscribe.bio/goldcheck.png" alt="Gold Check Mark" className="gold-check-mark" />
              )}
            </div>
            <div id="userName" className="bioName">
              {profileBio}
            </div>

            {subscriptionEnabled && subscriptionPrice && (
              <div className="subscribe-container">
                {!isSubscribed ? (
                  <>
                    <button
                      className="subscribe-button"
                      onClick={handleSubscribe}
                      disabled={isSubscribing}
                    >
                      {isSubscribing ? (
                        <CircularProgress size={20} style={{ color: 'white' }} />
                      ) : (
                        `Subscribe  $${subscriptionPrice}/month`
                      )}
                    </button>
                  </>
                ) : (
                  <div className="subscribed-badge">✓ Subscribed</div>
                )}
              </div>
            )}

            <div id="links">
              {userLinks.map((link, index) => (
                <a
                  key={index}
                  className="link"
                  href={
                    link.url.startsWith('http://') || link.url.startsWith('https://')
                      ? link.url
                      : `http://${link.url}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={`fab ${link.icon}`}>&nbsp;</i>
                  {link.title}
                </a>
              ))}
            </div>

            <Link
              to="/home"
              style={{ textDecoration: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              {!logo && (
                <button
                  style={{
                    width: '235px',
                    cursor: 'pointer',
                    background: 'white',
                    border: 'none',
                    borderRadius: '30px',
                    color: 'black',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    padding: '16px 20px',
                    boxShadow: '0px 4px 1px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <img className="navlogomobile2" src={navlogomobile} alt="" />
                  Create Your XSubscribe
                </button>
              )}
            </Link>
          </div>
        )}

        {backgroundType === 'Image' && backgroundpic && (
          <div className="profile-container1" style={backgroundStyle}>
            <section className="animated-background">
              <div id="stars1"></div>
              <div id="stars2"></div>
              <div id="stars3"></div>
            </section>

            <a id="profilePicture">
              {isProfilePicLoading ? (
                <CircularProgress size={30} style={{ color: 'white' }} />
              ) : profilepic ? (
                <img src={profilepic} alt="Profile" />
              ) : (
                <p className="profilenulltext">@</p>
              )}
            </a>

            <div id="userName" className="userName">
              {profileTitle}
              {verifiedStatus && (
                <img src="https://xsubscribe.bio/bluecheck.svg" alt="Blue Check Mark" className="blue-check-mark" />
              )}
              {premiumStatus && (
                <img src="https://xsubscribe.bio/goldcheck.png" alt="Gold Check Mark" className="gold-check-mark" />
              )}
            </div>
            <div id="userName" className="bioName">
              {profileBio}
            </div>

            {subscriptionEnabled && subscriptionPrice && (
              <div className="subscribe-container">
                {!isSubscribed ? (
                  <>
                    <button
                      className="subscribe-button"
                      onClick={handleSubscribe}
                      disabled={isSubscribing}
                    >
                      {isSubscribing ? (
                        <CircularProgress size={20} style={{ color: 'white' }} />
                      ) : (
                        `Subscribe  $${subscriptionPrice}/month`
                      )}
                    </button>
                  </>
                ) : (
                  <div className="subscribed-badge">✓ Subscribed</div>
                )}
              </div>
            )}

            <div id="links">
              {userLinks.map((link, index) => (
                <a
                  key={index}
                  className="link"
                  href={
                    link.url.startsWith('http://') || link.url.startsWith('https://')
                      ? link.url
                      : `http://${link.url}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={`fab ${link.icon}`}>&nbsp;</i>
                  {link.title}
                </a>
              ))}
            </div>

            <Link
              to="/home"
              style={{ textDecoration: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              {!logo && (
                <button
                  style={{
                    width: '235px',
                    cursor: 'pointer',
                    background: 'white',
                    border: 'none',
                    borderRadius: '30px',
                    color: 'black',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    padding: '16px 20px',
                    boxShadow: '0px 4px 1px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <img className="navlogomobile2" src={navlogomobile} alt="" />
                  Create Your XSubcribe
                </button>
              )}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
